import { useState } from "react";
import * as Sentry from "@sentry/react";
import { C } from "../lib/theme";
import { Btn } from "../components/Btn";
import { Icon } from "../components/Icon";
import { KapioLogo } from "../components/KapioLogo";
import { supabase } from "../lib/supabase";
import { isPhoneAuthEnabled } from "../lib/featureFlags";

// Record a failed login attempt via the rate-limited Edge Function. The
// underlying RPC `record_failed_login_attempt` had its anon/authenticated
// grants revoked in migration 20260507000004 — direct RPC calls from the
// client would let anyone with the anon key DoS-lockout an arbitrary
// victim. The Edge Function (verify_jwt: false) wraps the RPC with a
// per-IP global + per-IP-per-target rate limit before forwarding.
//
// Best-effort: a network failure here never masks the underlying auth
// result.
async function recordLoginFailure(identifier, type) {
  try {
    const url = import.meta.env.VITE_SUPABASE_URL;
    const anon = import.meta.env.VITE_SUPABASE_ANON_KEY;
    if (!url || !anon) return;
    await fetch(`${url}/functions/v1/record-login-failure`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: anon,
        Authorization: `Bearer ${anon}`,
      },
      body: JSON.stringify({ identifier, type }),
    });
  } catch (err) {
    console.warn("recordLoginFailure failed", err);
  }
}

export function Login({ onBackToHome } = {}) {
  // Modes: signin | signup | reset | otp
  const [mode, setMode] = useState("signin");
  const [role, setRole] = useState("customer"); // customer | business (signup only)
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [storeName, setStoreName] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [resetSent, setResetSent] = useState(false);

  // Phone OTP login state. Tab toggle on the signin screen, gated on the
  // VITE_PHONE_AUTH_ENABLED feature flag. Inactive until Africa's Talking
  // SMS hook secrets are set in production.
  const phoneAuthEnabled = isPhoneAuthEnabled();
  const [signinChannel, setSigninChannel] = useState("email"); // email | phone
  const [phoneStep, setPhoneStep] = useState("input"); // input | otp
  const [loginPhone, setLoginPhone] = useState("");
  const [phoneOtpCode, setPhoneOtpCode] = useState("");
  const [phoneSubmittedFor, setPhoneSubmittedFor] = useState("");

  function clearError() {
    if (error) setError(null);
  }

  async function onSignIn(e) {
    e.preventDefault();
    setBusy(true);
    setError(null);

    // Account lockout — mirrors mobile authService.signInWithEmail.
    // Identifier matches the RPC's lower()-on-write so the lockout key is
    // the same regardless of input casing.
    const identifier = email.trim().toLowerCase();

    // Pre-check lockout before calling auth.
    const { data: lockData } = await supabase.rpc("check_login_lockout", {
      p_identifier: identifier,
      p_type: "password",
    });
    if (lockData?.locked) {
      setBusy(false);
      setError(
        `Account locked until ${new Date(lockData.locked_until).toLocaleTimeString()}. Try resetting your password.`
      );
      return;
    }

    const { error: err } = await supabase.auth.signInWithPassword({
      email: identifier,
      password,
    });
    setBusy(false);

    if (err) {
      // Best-effort — Edge Function failure must not mask the auth error.
      // Goes through record-login-failure to bypass the DoS-lockout vector.
      await recordLoginFailure(identifier, "password");
      setError(err.message || "Unable to sign in");
      return;
    }

    // Best-effort — RPC failure must not mask the auth success.
    try {
      await supabase.rpc("clear_login_lockout", {
        p_identifier: identifier,
        p_type: "password",
      });
    } catch (rpcErr) {
      console.warn("clear_login_lockout failed", rpcErr);
    }
  }

  async function onSendPhoneOtp(e) {
    e.preventDefault();
    const normalized = loginPhone.trim().replace(/\s+/g, "");
    if (!/^\+\d{10,14}$/.test(normalized)) {
      setError("Enter phone in international format (e.g. +260971234567).");
      return;
    }
    setBusy(true);
    setError(null);

    // Pre-check OTP-typed lockout — shares the same RPC the email path uses,
    // but with p_type: 'otp' so the counters don't bleed into one another.
    const { data: lockData } = await supabase.rpc("check_login_lockout", {
      p_identifier: normalized,
      p_type: "otp",
    });
    if (lockData?.locked) {
      setBusy(false);
      setError("Too many code requests. Try again later.");
      return;
    }

    const { error: err } = await supabase.auth.signInWithOtp({ phone: normalized });
    setBusy(false);
    if (err) {
      setError(err.message || "Failed to send code. Please try again.");
      return;
    }
    setPhoneSubmittedFor(normalized);
    setPhoneStep("otp");
  }

  async function onVerifyPhoneOtp(e) {
    e.preventDefault();
    if (phoneOtpCode.length !== 6) return;
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.auth.verifyOtp({
      phone: phoneSubmittedFor,
      token: phoneOtpCode,
      type: "sms",
    });
    setBusy(false);
    if (err) {
      // Goes through the Edge Function — see recordLoginFailure docstring.
      await recordLoginFailure(phoneSubmittedFor, "otp");
      setError("Invalid or expired code. Try again.");
      return;
    }
    try {
      await supabase.rpc("clear_login_lockout", {
        p_identifier: phoneSubmittedFor,
        p_type: "otp",
      });
    } catch (rpcErr) {
      console.warn("clear_login_lockout failed", rpcErr);
    }
    // onAuthStateChange("SIGNED_IN") handles routing — same as email path.
  }

  async function onResendPhoneOtp() {
    if (!phoneSubmittedFor) return;
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.auth.signInWithOtp({ phone: phoneSubmittedFor });
    setBusy(false);
    if (err) setError(err.message || "Failed to resend code.");
  }

  function onChangePhone() {
    setPhoneStep("input");
    setPhoneOtpCode("");
    setPhoneSubmittedFor("");
    clearError();
  }

  async function onReset(e) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}`,
    });
    setBusy(false);
    if (err) setError(err.message || "Unable to send reset email");
    else setResetSent(true);
  }

  async function onSignUp(e) {
    e.preventDefault();
    if (!firstName.trim()) {
      setError("First name is required.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (!/[a-zA-Z]/.test(password) || !/[0-9]/.test(password)) {
      setError("Password must contain at least one letter and one number.");
      return;
    }
    if (role === "business" && !storeName.trim()) {
      setError("Store name is required for seller accounts.");
      return;
    }
    setBusy(true);
    setError(null);
    const name = [firstName.trim(), lastName.trim()].filter(Boolean).join(" ");
    const metadata = {
      name,
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      phone: phone.trim() ? (phone.trim().startsWith("+") ? phone.trim() : `+260${phone.trim()}`) : null,
      role,
      store_name: role === "business" ? storeName.trim() : null,
    };
    const { data, error: err } = await supabase.auth.signUp({
      email: email.trim().toLowerCase(),
      password,
      options: { data: metadata },
    });
    setBusy(false);
    if (err) {
      const msg = (err.message || "").toLowerCase();
      if (msg.includes("already registered")) {
        setError("This email is already registered. Try signing in instead.");
      } else if (msg.includes("leaked") || msg.includes("easy to guess")) {
        setError("This password has appeared in a known data breach. Please choose a stronger, unique password.");
      } else {
        setError(err.message || "Unable to create account");
      }
      return;
    }
    // If Supabase email confirmation is enabled, data.session will be null and
    // the user needs to verify via OTP. If it's disabled, we're already signed
    // in and the onAuthStateChange SIGNED_IN hydration will take over.
    if (!data?.session) {
      setMode("otp");
      return;
    }
    // Signed in immediately — if it's a business account, create the seller row.
    if (role === "business" && data.user?.id) {
      try {
        await supabase.from("sellers").upsert(
          {
            user_id: data.user.id,
            store_name: storeName.trim() || `${firstName.trim()}'s Store`,
            status: "active",
            approved_at: new Date().toISOString(),
          },
          { onConflict: "user_id" }
        );
      } catch (err) {
        // Silent failure here leaves a "business" account without a seller row.
        // Surface to Sentry so we know if signup is dropping seller rows in prod.
        Sentry.captureException(err, { extra: { context: "signup.sellerUpsert", user_id: data.user.id } });
      }
    }
  }

  async function onVerifyOtp(e) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { data, error: err } = await supabase.auth.verifyOtp({
      email: email.trim().toLowerCase(),
      token: otp.trim(),
      type: "signup",
    });
    setBusy(false);
    if (err) {
      setError(err.message || "Invalid verification code");
      return;
    }
    // Seller row creation after email verification, if applicable.
    if (role === "business" && data?.user?.id) {
      try {
        await supabase.from("sellers").upsert(
          {
            user_id: data.user.id,
            store_name: storeName.trim() || `${firstName.trim()}'s Store`,
            status: "active",
            approved_at: new Date().toISOString(),
          },
          { onConflict: "user_id" }
        );
      } catch (err) {
        Sentry.captureException(err, { extra: { context: "verifyOtp.sellerUpsert", user_id: data.user.id } });
      }
    }
  }

  function goToMode(next) {
    setError(null);
    setResetSent(false);
    setMode(next);
  }

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: C.bg }}>
      <div style={{ width: "100%", maxWidth: 460 }}>
        {onBackToHome && (
          <button
            type="button"
            onClick={onBackToHome}
            style={{
              background: "none",
              border: "none",
              color: C.muted,
              fontSize: 12,
              fontWeight: 600,
              fontFamily: "inherit",
              cursor: "pointer",
              padding: "6px 0",
              marginBottom: 8,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Icon name="chevron-left" size={14} color={C.muted} />
            Back to home
          </button>
        )}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 24 }}>
          <KapioLogo size={36} />
          <span style={{ fontSize: 22, fontWeight: 900, color: C.hunter, letterSpacing: "-0.5px" }}>Kapio</span>
        </div>

        <div
          style={{
            background: C.white,
            border: "1px solid rgba(196,193,183,0.35)",
            borderRadius: 20,
            padding: 28,
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          }}
        >
          {mode === "signin" && (
            <>
              <h1 style={{ fontSize: 20, fontWeight: 900, color: C.charcoal, margin: 0, letterSpacing: "-0.3px" }}>
                Welcome back
              </h1>
              <p style={{ fontSize: 13, color: C.muted, marginTop: 6, marginBottom: 20, lineHeight: 1.5 }}>
                Sign in to your Kapio account.
              </p>

              {phoneAuthEnabled && (
                <div
                  style={{
                    display: "flex",
                    background: C.cream,
                    borderRadius: 12,
                    padding: 4,
                    marginBottom: 16,
                    gap: 4,
                  }}
                  role="tablist"
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={signinChannel === "email"}
                    onClick={() => {
                      setSigninChannel("email");
                      clearError();
                    }}
                    style={{
                      flex: 1,
                      padding: "9px 12px",
                      borderRadius: 9,
                      border: "none",
                      background: signinChannel === "email" ? C.white : "transparent",
                      color: signinChannel === "email" ? C.charcoal : C.muted,
                      fontFamily: "inherit",
                      fontSize: 13,
                      fontWeight: signinChannel === "email" ? 800 : 600,
                      cursor: "pointer",
                    }}
                  >
                    Email
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={signinChannel === "phone"}
                    onClick={() => {
                      setSigninChannel("phone");
                      clearError();
                    }}
                    style={{
                      flex: 1,
                      padding: "9px 12px",
                      borderRadius: 9,
                      border: "none",
                      background: signinChannel === "phone" ? C.white : "transparent",
                      color: signinChannel === "phone" ? C.charcoal : C.muted,
                      fontFamily: "inherit",
                      fontSize: 13,
                      fontWeight: signinChannel === "phone" ? 800 : 600,
                      cursor: "pointer",
                    }}
                  >
                    Phone
                  </button>
                </div>
              )}

              {(!phoneAuthEnabled || signinChannel === "email") && (
                <form onSubmit={onSignIn}>
                  <Field label="Email">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); clearError(); }}
                      autoComplete="email"
                      autoFocus
                      required
                      style={inputStyle}
                    />
                  </Field>

                  <Field label="Password">
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); clearError(); }}
                      autoComplete="current-password"
                      required
                      style={inputStyle}
                    />
                  </Field>

                  {error && <ErrorBanner message={error} />}

                  <div style={{ marginTop: 18 }}>
                    <Btn type="submit" full size="lg" disabled={busy || !email || !password}>
                      {busy ? "Signing in…" : "Sign in"}
                    </Btn>
                  </div>

                  <button type="button" onClick={() => goToMode("reset")} style={linkBtnStyle}>
                    Forgot password?
                  </button>
                </form>
              )}

              {phoneAuthEnabled && signinChannel === "phone" && phoneStep === "input" && (
                <form onSubmit={onSendPhoneOtp}>
                  <Field label="Phone number">
                    <input
                      type="tel"
                      value={loginPhone}
                      onChange={(e) => { setLoginPhone(e.target.value); clearError(); }}
                      autoComplete="tel"
                      autoFocus
                      required
                      placeholder="+260971234567"
                      style={inputStyle}
                    />
                  </Field>

                  {error && <ErrorBanner message={error} />}

                  <div style={{ marginTop: 18 }}>
                    <Btn type="submit" full size="lg" disabled={busy || !loginPhone}>
                      {busy ? "Sending…" : "Send code"}
                    </Btn>
                  </div>
                </form>
              )}

              {phoneAuthEnabled && signinChannel === "phone" && phoneStep === "otp" && (
                <form onSubmit={onVerifyPhoneOtp}>
                  <p style={{ fontSize: 13, color: C.muted, marginTop: 0, marginBottom: 14, lineHeight: 1.5 }}>
                    Enter the 6-digit code sent to <strong>{phoneSubmittedFor}</strong>.
                  </p>

                  <Field label="Verification code">
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={phoneOtpCode}
                      onChange={(e) => { setPhoneOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6)); clearError(); }}
                      autoFocus
                      required
                      maxLength={6}
                      placeholder="123456"
                      style={{ ...inputStyle, fontSize: 18, letterSpacing: 4, textAlign: "center", fontFamily: "ui-monospace, Menlo, monospace" }}
                    />
                  </Field>

                  {error && <ErrorBanner message={error} />}

                  <div style={{ marginTop: 18 }}>
                    <Btn type="submit" full size="lg" disabled={busy || phoneOtpCode.length !== 6}>
                      {busy ? "Verifying…" : "Verify"}
                    </Btn>
                  </div>

                  <button type="button" onClick={onResendPhoneOtp} disabled={busy} style={linkBtnStyle}>
                    {busy ? "Sending…" : "Resend code"}
                  </button>
                  <button type="button" onClick={onChangePhone} style={{ ...linkBtnStyle, paddingTop: 4 }}>
                    Change phone number
                  </button>
                </form>
              )}
            </>
          )}

          {mode === "signup" && (
            <form onSubmit={onSignUp}>
              <button type="button" onClick={() => goToMode("signin")} style={backBtnStyle}>
                <Icon name="chevron-left" size={14} color={C.muted} />
                Back to sign in
              </button>
              <h1 style={{ fontSize: 20, fontWeight: 900, color: C.charcoal, margin: "10px 0 0", letterSpacing: "-0.3px" }}>
                Create your account
              </h1>
              <p style={{ fontSize: 13, color: C.muted, marginTop: 6, marginBottom: 16, lineHeight: 1.5 }}>
                Join Kapio to shop or sell.
              </p>

              {/* Role toggle */}
              <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                <RoleOption active={role === "customer"} onClick={() => setRole("customer")} label="Shop" desc="Buy from local sellers" />
                <RoleOption active={role === "business"} onClick={() => setRole("business")} label="Sell" desc="Open a store" />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <Field label="First name">
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => { setFirstName(e.target.value); clearError(); }}
                    autoComplete="given-name"
                    required
                    style={inputStyle}
                  />
                </Field>
                <Field label="Last name">
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => { setLastName(e.target.value); clearError(); }}
                    autoComplete="family-name"
                    style={inputStyle}
                  />
                </Field>
              </div>

              <Field label="Email">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); clearError(); }}
                  autoComplete="email"
                  required
                  style={inputStyle}
                />
              </Field>

              <Field label="Password">
                <input
                  type="password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); clearError(); }}
                  autoComplete="new-password"
                  required
                  minLength={8}
                  style={inputStyle}
                />
              </Field>

              <Field label="Phone (optional)">
                <div style={{ display: "flex", alignItems: "center", gap: 8, background: C.cream, border: "1px solid rgba(196,193,183,0.4)", borderRadius: 12, padding: "0 13px" }}>
                  <span style={{ fontSize: 13, color: C.muted, fontWeight: 600 }}>+260</span>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => { setPhone(e.target.value.replace(/[^\d+]/g, "")); clearError(); }}
                    autoComplete="tel"
                    placeholder="97 1234567"
                    style={{ ...inputStyle, background: "transparent", border: "none", padding: "11px 0", flex: 1 }}
                  />
                </div>
              </Field>

              {role === "business" && (
                <Field label="Store name">
                  <input
                    type="text"
                    value={storeName}
                    onChange={(e) => { setStoreName(e.target.value); clearError(); }}
                    required
                    placeholder="e.g. Kapio Threads"
                    style={inputStyle}
                  />
                </Field>
              )}

              {error && <ErrorBanner message={error} />}

              <div style={{ marginTop: 18 }}>
                <Btn type="submit" full size="lg" disabled={busy || !email || !password || !firstName.trim() || (role === "business" && !storeName.trim())}>
                  {busy ? "Creating account…" : "Create account"}
                </Btn>
              </div>

              <p style={{ fontSize: 11, color: C.muted, marginTop: 12, lineHeight: 1.5, textAlign: "center" }}>
                By creating an account you agree to Kapio's{" "}
                <a href="https://kapio-apps.pages.dev/terms.html" target="_blank" rel="noreferrer" style={{ color: C.hunter, fontWeight: 600, textDecoration: "none" }}>Terms</a>
                {" "}and{" "}
                <a href="https://kapio-apps.pages.dev/privacy.html" target="_blank" rel="noreferrer" style={{ color: C.hunter, fontWeight: 600, textDecoration: "none" }}>Privacy Policy</a>.
              </p>
            </form>
          )}

          {mode === "otp" && (
            <form onSubmit={onVerifyOtp}>
              <h1 style={{ fontSize: 20, fontWeight: 900, color: C.charcoal, margin: 0, letterSpacing: "-0.3px" }}>
                Verify your email
              </h1>
              <p style={{ fontSize: 13, color: C.muted, marginTop: 6, marginBottom: 20, lineHeight: 1.5 }}>
                We sent a 6-digit code to <strong>{email}</strong>. Enter it below to activate your account.
              </p>

              <Field label="Verification code">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={otp}
                  onChange={(e) => { setOtp(e.target.value.replace(/\D/g, "").slice(0, 6)); clearError(); }}
                  autoFocus
                  required
                  maxLength={6}
                  placeholder="123456"
                  style={{ ...inputStyle, fontSize: 18, letterSpacing: 4, textAlign: "center", fontFamily: "ui-monospace, Menlo, monospace" }}
                />
              </Field>

              {error && <ErrorBanner message={error} />}

              <div style={{ marginTop: 18 }}>
                <Btn type="submit" full size="lg" disabled={busy || otp.length !== 6}>
                  {busy ? "Verifying…" : "Verify & continue"}
                </Btn>
              </div>

              <button type="button" onClick={() => goToMode("signup")} style={linkBtnStyle}>
                Back to sign up
              </button>
            </form>
          )}

          {mode === "reset" && (
            <form onSubmit={onReset}>
              <button type="button" onClick={() => goToMode("signin")} style={backBtnStyle}>
                <Icon name="chevron-left" size={14} color={C.muted} />
                Back to sign in
              </button>
              <h1 style={{ fontSize: 20, fontWeight: 900, color: C.charcoal, margin: "10px 0 0", letterSpacing: "-0.3px" }}>
                Reset password
              </h1>
              <p style={{ fontSize: 13, color: C.muted, marginTop: 6, marginBottom: 20, lineHeight: 1.5 }}>
                We'll email you a link to set a new password.
              </p>

              <Field label="Email">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); clearError(); }}
                  autoComplete="email"
                  autoFocus
                  required
                  style={inputStyle}
                />
              </Field>

              {error && <ErrorBanner message={error} />}

              {resetSent && (
                <div
                  style={{
                    marginTop: 14,
                    padding: "10px 12px",
                    background: C.greenBg,
                    border: `1px solid ${C.hunter}`,
                    borderRadius: 10,
                    fontSize: 12.5,
                    color: C.hunter,
                    fontWeight: 600,
                  }}
                >
                  Check your inbox — reset link sent to {email}.
                </div>
              )}

              <div style={{ marginTop: 18 }}>
                <Btn type="submit" full size="lg" disabled={busy || !email}>
                  {busy ? "Sending…" : "Send reset link"}
                </Btn>
              </div>
            </form>
          )}
        </div>

        {mode === "signin" && (
          <div style={{ textAlign: "center", fontSize: 12, color: C.muted, marginTop: 20, lineHeight: 1.6 }}>
            New to Kapio?{" "}
            <button type="button" onClick={() => goToMode("signup")} style={{ background: "none", border: "none", color: C.hunter, fontWeight: 700, cursor: "pointer", fontSize: 12, fontFamily: "inherit", padding: 0 }}>
              Create an account
            </button>
          </div>
        )}
        {mode === "signup" && (
          <div style={{ textAlign: "center", fontSize: 12, color: C.muted, marginTop: 20, lineHeight: 1.6 }}>
            Already have an account?{" "}
            <button type="button" onClick={() => goToMode("signin")} style={{ background: "none", border: "none", color: C.hunter, fontWeight: 700, cursor: "pointer", fontSize: 12, fontFamily: "inherit", padding: 0 }}>
              Sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function RoleOption({ active, onClick, label, desc }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: 1,
        padding: "12px 14px",
        borderRadius: 12,
        border: active ? `2px solid ${C.hunter}` : "1.5px solid rgba(196,193,183,0.6)",
        background: active ? C.greenBg : C.white,
        cursor: "pointer",
        textAlign: "left",
        fontFamily: "inherit",
        transition: "all 120ms ease",
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 800, color: active ? C.hunter : C.charcoal }}>{label}</div>
      <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{desc}</div>
    </button>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block", marginBottom: 12 }}>
      <div style={{ fontSize: 11.5, fontWeight: 800, color: C.slate, marginBottom: 6, letterSpacing: 0.2 }}>{label}</div>
      {children}
    </label>
  );
}

function ErrorBanner({ message }) {
  return (
    <div
      style={{
        marginTop: 14,
        padding: "10px 12px",
        background: C.redBg,
        border: `1px solid ${C.red}`,
        borderRadius: 10,
        fontSize: 12.5,
        color: C.red,
        fontWeight: 600,
      }}
    >
      {message}
    </div>
  );
}

const inputStyle = {
  width: "100%",
  padding: "11px 13px",
  fontSize: 13.5,
  fontFamily: "inherit",
  background: C.cream,
  border: "1px solid rgba(196,193,183,0.4)",
  borderRadius: 12,
  color: C.charcoal,
  outline: "none",
  boxSizing: "border-box",
};

const linkBtnStyle = {
  background: "none",
  border: "none",
  color: C.muted,
  fontSize: 12.5,
  fontWeight: 700,
  cursor: "pointer",
  padding: "12px 0 0",
  fontFamily: "inherit",
  display: "block",
  width: "100%",
  textAlign: "center",
};

const backBtnStyle = {
  background: "none",
  border: "none",
  color: C.muted,
  fontSize: 12,
  fontWeight: 700,
  cursor: "pointer",
  padding: 0,
  fontFamily: "inherit",
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
};
